import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, Columns, Container } from '../../components/base/grid';
import { H1, P } from '../../components/base/typography';
import Breadcrumbs from '../../components/breadcrumbs';
import CtaBoxColumns from '../../components/ctaBoxes/ctaBoxColumns';
import { RawLoader } from '../../components/loading/loading';
import ShowcaseSection from '../../components/showcaseSection';
import { API_BASE_URL } from '../../constants/paths/api';
import { iconDropdownMapper } from '../../constants/styleUtils/iconDropdownMapper';
import { getCurrentCulture } from '../../helpers/url/urlHelpers';
import useGet from '../../hooks/useGet';
import { IShowcaseSection } from '../../interfaces/umbracoNodes/contentSection';
import IPageViewModel from '../../interfaces/umbracoNodes/pageViewModel';
import { routeIconMapper } from './routeIconMapper';
import * as StyledSubRoute from './subRoute.styles';

interface Props {
	page: IPageViewModel;
}

const SubRouteFragment: FC<Props> = ({ page }) => {
	const { t } = useTranslation('common');
	const { response } = useGet(`${API_BASE_URL}/page/${page.Id}/children?lang=${getCurrentCulture()}`);

	if (!response) return <RawLoader />;
	if (page.Values.StyleIcons)
		return (
			<Container>
				<Breadcrumbs pageId={page.Id} />
				{response.data.slice(0, 2).map((page, index) => {
					const showcase: IShowcaseSection = {
						Id: page.Id,
						Name: page.Name,
						NavigationTitle: page.Name,
						Type: page.Type,
						Url: page.Url,
						CulturePaths: null,
						Values: {
							ImageLeft: index % 2 !== 0,
							NavigationTitle: page.Name || '',
							Content: page.Values.PageSummary || '',
							ContainImage: false,
							Image: page.Values.PagePicture || { Src: '' },
							Title: page.Name || '',
							PageTitle: page.Values.PageTitle || '',
							Button: {
								Name: page.Values.ButtonRedirectText || '',
								Target: null,
								Type: null,
								Url: page.Url,
							},
						},
					};
					return (
						<Fragment key={index}>
							<ShowcaseSection content={showcase} />
						</Fragment>
					);
				})}
				<CtaBoxColumns boxes={page.Values.CtaBoxes} />
			</Container>
		);

	return (
		<Container>
			<Breadcrumbs pageId={page.Id} />
			<H1 marginBottom="4rem" marginTop="4rem">
				{page.Name}
			</H1>
			{response && (
				<StyledSubRoute.StyledSubRouteLinksWrapper>
					<Columns>
						{response.data.map((child: IPageViewModel) => {
							// Should this be in the backend instead?
							if (child.Values.HideFromNavigation || child.Name === null || child.Url === '#')
								return null;

							const Icon =
								iconDropdownMapper[child.Values.PageIcon] ||
								routeIconMapper[child.Type] ||
								routeIconMapper.contentSite;
							return (
								<Column
									marginBottom="3rem"
									mobile={1}
									tablet={1 / 2}
									desktopSmall={1 / 3}
									key={child.Id}>
									<StyledSubRoute.SubRouteLink to={child.Url}>
										{child.Values.PagePicture && (
											<StyledSubRoute.SubRoutePictureWrapper
												image={`${child.Values.PagePicture.Src}&mode=max`}
												hidePlaceholder
											/>
										)}
										<StyledSubRoute.ContentWrapper paddingTop={!child.Values.PagePicture}>
											<div>
												{!child.Values.PagePicture && (
													<StyledSubRoute.SubRouteIconWrapper>
														<Icon />
													</StyledSubRoute.SubRouteIconWrapper>
												)}
												<StyledSubRoute.SubRouteName>
													{child.Values.PageTitle}
												</StyledSubRoute.SubRouteName>
												<P>{child.Values.PageSummary}</P>
											</div>
											<StyledSubRoute.SubRouteButton>{t('more')}</StyledSubRoute.SubRouteButton>
										</StyledSubRoute.ContentWrapper>
									</StyledSubRoute.SubRouteLink>
								</Column>
							);
						})}
					</Columns>
				</StyledSubRoute.StyledSubRouteLinksWrapper>
			)}
		</Container>
	);
};

export default SubRouteFragment;
